import { CommonModule } from '@angular/common';
import { afterNextRender, Component, computed, ElementRef, input, viewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';

/**
 * Native port of the AngularJS `ftTextareaField` field-editor directive — the textarea
 * sibling of {@link FtTextFieldComponent}: a multi-language branch (one `.modal-panel` per
 * language with a `<textarea>` bound to `fieldData[field.name]`) plus a single-datasource
 * branch. `maxLength` renders as the cosmetic `[attr.ng-maxlength]`; `height` → `inlineStyle`.
 */
@Component({
    selector: 'ft-textarea-field-ng',
    imports: [CommonModule, FormsModule],
    templateUrl: './ft-textarea-field.component.html'
})
export class FtTextareaFieldComponent {
    /** The object/array whose textarea fields the inputs edit. Array → multi-language branch. */
    readonly datasource = input<any>();
    /** A field name, or `{name,label}`, or a list of either — normalised below. */
    readonly fields = input<any>();
    /** The v1 `attrs`-derived constraints, passed as a raw object (e.g. `{ height: 500 }`). */
    readonly fieldsOptions = input<any>();

    /** The single-datasource textareas — v1 marks these `ft-focus` (the multi-language branch is not focused). */
    private readonly singleTextareas = viewChildren<ElementRef<HTMLTextAreaElement>>('singleTextarea');

    constructor() {
        // v1 textAreaField.html single-datasource branch: `<textarea ft-focus />` → $timeout +
        // focus(). Deferred past ngModel's initial writeValue so the caret lands at the end of
        // the pre-set content (Chrome then scrolls long content to the bottom, as v1 did).
        afterNextRender(() => {
            setTimeout(() => {
                for (const ta of this.singleTextareas()) ta.nativeElement.focus();
            });
        });
    }

    // v1 textAreaField.js link: isMultiLanguage = angular.isArray(datasource).
    protected readonly isMultiLanguage = computed<boolean>(() => Array.isArray(this.datasource()));
    protected readonly languages = computed<any[]>(() => (this.isMultiLanguage() ? this.datasource() : []));
    /** Stable per-language rows: { language, fieldData } (v1 `ng-init` fieldData), for [(ngModel)]. */
    protected readonly rows = computed<Array<{ language: any; fieldData: any }>>(() =>
        // v1 `ng-init="fieldData = language.data ? language.data : language"` — precompute stable refs.
        this.languages().map((l: any) => ({ language: l, fieldData: l && l.data ? l.data : l }))
    );

    protected readonly normalizedFields = computed<Array<{ name: string; label: string | null }>>(() => {
        // v1 `adjustFields`: coerce string entries to {name,label:null}.
        const fields = this.fields();
        const raw = Array.isArray(fields) ? fields : fields == null ? [] : [fields];
        return raw.map((f: any) => (typeof f === 'string' ? { name: f, label: null } : f));
    });

    // v1 read attrs.required / attrs.ngMinlength / attrs.ngMaxlength / attrs.height.
    // v1 link-fn scope defaults.
    protected readonly required = computed<boolean>(() => {
        const o = this.fieldsOptions() || {};
        return (o['required'] != null && o['required'] !== false && o['required'] !== 'false') || false;
    });
    protected readonly minLength = computed<number>(() => {
        const o = this.fieldsOptions() || {};
        return o['ng-minlength'] != null ? +o['ng-minlength'] : 0;
    });
    protected readonly maxLength = computed<number>(() => {
        const o = this.fieldsOptions() || {};
        return o['ng-maxlength'] != null ? +o['ng-maxlength'] : 1073741824; // 1024^3 (v1 default)
    });
    protected readonly inlineStyle = computed<{ [k: string]: string } | null>(() => {
        // v1 `if (isDefined(attrs.height)) inlineStyle = {max-height:none, height:Npx}`.
        const o = this.fieldsOptions() || {};
        return o['height'] != null ? { 'max-height': 'none', height: o['height'] + 'px' } : null;
    });
}
